import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { nip19 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';
import { useNostr } from '@nostrify/react';

import { AppShell } from '@/components/AppShell';
import { useDittoThemeContext } from '@/components/DittoThemeProvider';
import { PolaroidCard } from '@/components/PolaroidCard';
import { PhotoDetail } from '@/components/PhotoDetail';
import { NoteContent } from '@/components/NoteContent';
import { Skeleton } from '@/components/ui/skeleton';
import { useSearchProfiles, type SearchProfile } from '@/hooks/useSearchProfiles';
import { useSearchPosts } from '@/hooks/useSearchPosts';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { formatPolaroidDate, seedFromHex, getImageUrl, PASTEL_STRIP_COLORS } from '@/lib/polaroid-utils';
import { cn } from '@/lib/utils';

type TabType = 'posts' | 'accounts';

const VALID_TABS: TabType[] = ['posts', 'accounts'];

function parseTab(value: string | null): TabType {
  return VALID_TABS.includes(value as TabType) ? (value as TabType) : 'posts';
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = parseTab(searchParams.get('tab'));
  const initialQuery = searchParams.get('q') ?? '';

  // The raw input state lives here so typing doesn't re-render the whole page.
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(initialQuery);
  const [selectedPhoto, setSelectedPhoto] = useState<NostrEvent | null>(null);

  // Guard against URL→state feedback loop
  const internalUrlUpdate = useRef(false);

  useSeoMeta({
    title: debouncedSearchQuery ? `Search: ${debouncedSearchQuery} | Polaroids` : 'Search | Polaroids',
    description: 'Search Polaroids for photos and people',
  });

  // Sync search query → URL
  useEffect(() => {
    const trimmed = debouncedSearchQuery.trim();
    internalUrlUpdate.current = true;
    setSearchParams((prev) => {
      const currentQ = prev.get('q') ?? '';
      if (trimmed === currentQ) {
        internalUrlUpdate.current = false;
        return prev;
      }
      const next = new URLSearchParams(prev);
      if (trimmed) {
        next.set('q', trimmed);
      } else {
        next.delete('q');
      }
      return next;
    }, { replace: true });
  }, [debouncedSearchQuery, setSearchParams]);

  // Sync URL → state (browser nav)
  useEffect(() => {
    if (internalUrlUpdate.current) {
      internalUrlUpdate.current = false;
      return;
    }
    const q = searchParams.get('q') ?? '';
    if (q !== debouncedSearchQuery.trim()) {
      setDebouncedSearchQuery(q);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update tab in URL
  const setActiveTab = useCallback((tab: TabType) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'posts') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const { hasBgImage } = useDittoThemeContext();

  // Data hooks
  const { data: profiles, isLoading: profilesLoading, followedPubkeys } = useSearchProfiles(activeTab === 'accounts' ? debouncedSearchQuery : '');
  const { data: postsResult, isLoading: postsLoading } = useSearchPosts(activeTab === 'posts' ? debouncedSearchQuery : '');

  const pictures = postsResult?.pictures ?? [];
  const comments = postsResult?.comments ?? [];

  return (
    <AppShell>
      {/* Search page header — hand-drawn sticky note style */}
      <div className="pt-2 pb-3">
        {/* Page title with doodle — gets paper-strip bg for legibility */}
        <div className="flex items-center gap-2 mb-3">
          <div className={cn(
            'flex items-center gap-2 px-2 py-0.5',
            hasBgImage ? 'paper-strip' : '',
          )} style={!hasBgImage ? { backgroundColor: PASTEL_STRIP_COLORS[1] } : undefined}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-pol-text">
              <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="2.2" />
              <path d="M15.5 15.5 L22 22" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
            <h1 className="font-hand text-2xl text-pol-text">Search</h1>
          </div>
        </div>

        {/* Search input */}
        <SearchInput
          initialValue={debouncedSearchQuery}
          onDebouncedChange={setDebouncedSearchQuery}
        />

        {/* Tabs — tape-style tab selector */}
        <div className="flex gap-2 mt-3">
          <TabButton
            label="Photos"
            active={activeTab === 'posts'}
            onClick={() => setActiveTab('posts')}
            tapeColor="var(--tape-0-solid)"
          />
          <TabButton
            label="People"
            active={activeTab === 'accounts'}
            onClick={() => setActiveTab('accounts')}
            tapeColor="var(--tape-4-solid)"
          />
        </div>
      </div>

      {/* ─── Posts Tab ─── */}
      {activeTab === 'posts' && (
        <div className="space-y-4">
          {/* Pictures section */}
          {postsLoading && pictures.length === 0 && comments.length === 0 ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <PostSkeleton key={i} />
              ))}
            </div>
          ) : (
            <>
              {/* Kind 20 pictures */}
              {pictures.length > 0 && (
                <div>
                  <p className="font-hand text-lg text-pol-text mb-2 paper-strip-pastel inline-block">Polaroids</p>
                  <div className="grid grid-cols-2 gap-3">
                    {pictures.map((event, i) => (
                      <PolaroidCard
                        key={event.id}
                        event={event}
                        variant="feed-grid"
                        index={i}
                        onPhotoClick={setSelectedPhoto}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Kind 1111 comments */}
              {comments.length > 0 && (
                <div>
                  <p className="font-hand text-lg text-pol-text mb-2 paper-strip-pastel inline-block">Comments</p>
                  <div className="space-y-2">
                    {comments.map((event) => (
                      <CommentResult key={event.id} event={event} onOpenParent={setSelectedPhoto} />
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {debouncedSearchQuery.trim() && pictures.length === 0 && comments.length === 0 && !postsLoading && (
                <EmptySearchState query={debouncedSearchQuery} type="posts" />
              )}

              {/* Prompt to search */}
              {!debouncedSearchQuery.trim() && (
                <EmptySearchState query="" type="prompt" />
              )}
            </>
          )}
        </div>
      )}

      {/* ─── Accounts Tab ─── */}
      {activeTab === 'accounts' && (
        <div>
          {debouncedSearchQuery.trim() ? (
            profilesLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <AccountSkeleton key={i} />
                ))}
              </div>
            ) : profiles && profiles.length > 0 ? (
              <div className="space-y-2">
                {profiles.map((profile) => (
                  <AccountItem
                    key={profile.pubkey}
                    profile={profile}
                    isFollowed={followedPubkeys.has(profile.pubkey)}
                  />
                ))}
              </div>
            ) : (
              <EmptySearchState query={debouncedSearchQuery} type="accounts" />
            )
          ) : (
            <EmptySearchState query="" type="prompt" />
          )}
        </div>
      )}

      {/* Photo detail overlay */}
      {selectedPhoto && (
        <PhotoDetail
          event={selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
        />
      )}
    </AppShell>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

/**
 * Debounced search input that owns its own keystroke state.
 */
function SearchInput({
  initialValue,
  onDebouncedChange,
}: {
  initialValue: string;
  onDebouncedChange: (value: string) => void;
}) {
  const [value, setValue] = useState(initialValue);
  const onDebouncedChangeRef = useRef(onDebouncedChange);
  onDebouncedChangeRef.current = onDebouncedChange;

  // Sync if the parent resets the value
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // Debounce
  useEffect(() => {
    const id = setTimeout(() => onDebouncedChangeRef.current(value), 300);
    return () => clearTimeout(id);
  }, [value]);

  return (
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="7.5" cy="7.5" r="5.5" stroke="currentColor" strokeWidth="2" className="text-pol-text-secondary" />
          <path d="M11.5 11.5 L16.5 16.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="text-pol-text-secondary" />
        </svg>
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Search photos and people..."
        className="w-full pl-10 pr-4 py-3 bg-polaroid-white text-pol-text font-hand text-lg placeholder:text-pol-text-secondary/50 outline-none border-2 border-dashed border-pol-text/15 focus:border-pol-pink/40 transition-colors shadow-[0_2px_8px_rgba(45,27,78,0.06)]"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        autoFocus
      />
      {value && (
        <button
          onClick={() => setValue('')}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-pol-text-secondary hover:text-pol-text transition-colors"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 2 L10 10 M10 2 L2 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </div>
  );
}

/** Tape-style tab button — both tabs always have a paper card; only the active one gets washi tape. */
function TabButton({
  label,
  active,
  onClick,
  tapeColor,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  tapeColor: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'relative px-5 py-2 font-hand text-base bg-polaroid-white shadow-[0_1px_4px_rgba(45,27,78,0.08)] transition-all',
        active
          ? 'text-pol-text font-bold shadow-[0_2px_6px_rgba(45,27,78,0.12)]'
          : 'text-pol-text-secondary hover:text-pol-text opacity-70 hover:opacity-100',
      )}
    >
      {/* Tape accent on top — only active */}
      {active && (
        <div
          className="absolute top-0 left-1/2 w-[40px] h-[10px]"
          style={{
            backgroundColor: tapeColor,
            opacity: 0.7,
            transform: 'translateX(-50%) translateY(-50%) rotate(-1.5deg)',
          }}
        />
      )}
      {label}
    </button>
  );
}

/** Account item — polaroid-style profile card */
function AccountItem({ profile, isFollowed }: { profile: SearchProfile; isFollowed: boolean }) {
  const npub = useMemo(() => nip19.npubEncode(profile.pubkey), [profile.pubkey]);
  const metadata = profile.metadata;
  const displayName = metadata?.name || metadata?.display_name || genUserName(profile.pubkey);
  const nip05 = metadata?.nip05;
  const nip05Display = nip05 ? (nip05.startsWith('_@') ? nip05.slice(2) : nip05) : undefined;
  const identifier = nip05Display || `${npub.slice(0, 16)}...`;

  // Deterministic pastel strip color
  const pastelIdx = seedFromHex(profile.pubkey, 0, 4) % 5;
  const pastelColor = PASTEL_STRIP_COLORS[pastelIdx];

  return (
    <Link
      to={`/${npub}`}
      className="flex items-center gap-3 px-3 py-3 bg-polaroid-white shadow-[0_1px_6px_rgba(45,27,78,0.08)] hover:shadow-[0_3px_12px_rgba(45,27,78,0.12)] transition-all hover:-translate-y-px"
      style={{ transform: `rotate(${(seedFromHex(profile.pubkey, 4, 6) % 3) - 1}deg)` }}
    >
      {/* Tape accent */}
      <div
        className="absolute left-0 top-1/2 -translate-y-1/2 w-[10px] h-[24px]"
        style={{
          backgroundColor: pastelColor,
          opacity: 0.6,
          transform: 'translateX(-3px)',
        }}
      />

      {/* Avatar */}
      <div className="relative shrink-0">
        <div className="w-11 h-11 rounded-full overflow-hidden bg-cream-dark border-2 border-pol-pink/20">
          {metadata?.picture ? (
            <img src={metadata.picture} alt={displayName} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-pol-pink/30 to-pol-lilac/30 flex items-center justify-center">
              <span className="font-hand text-lg text-pol-text-secondary">{displayName[0]?.toUpperCase() || '?'}</span>
            </div>
          )}
        </div>
        {isFollowed && (
          <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-pol-pink flex items-center justify-center ring-2 ring-polaroid-white">
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
              <path d="M1.5 4 L3 5.5 L6.5 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="font-hand text-base text-pol-text truncate leading-tight font-bold">{displayName}</p>
        <p className="text-[11px] text-pol-text-secondary truncate font-sans">{identifier}</p>
        {metadata?.about && (
          <p className="text-xs text-pol-text-secondary/70 mt-0.5 line-clamp-1 font-sans">{metadata.about}</p>
        )}
      </div>
    </Link>
  );
}

/** A kind 1111 comment result — clicking opens the parent polaroid. */
function CommentResult({ event, onOpenParent }: { event: NostrEvent; onOpenParent: (parentEvent: NostrEvent) => void }) {
  const { nostr } = useNostr();
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? genUserName(event.pubkey);
  const npub = useMemo(() => nip19.npubEncode(event.pubkey), [event.pubkey]);
  const date = formatPolaroidDate(event.created_at);

  // Resolve the parent kind 20 event ID from comment tags
  // NIP-1111: uppercase E = root event, lowercase e = immediate parent
  const parentEventId = useMemo(() => {
    const rootE = event.tags.find(([name]) => name === 'E')?.[1];
    if (rootE) return rootE;
    // Fallback: last lowercase 'e' tag (immediate parent)
    const eTags = event.tags.filter(([name]) => name === 'e');
    return eTags.length > 0 ? eTags[eTags.length - 1][1] : undefined;
  }, [event.tags]);

  const [isLoadingParent, setIsLoadingParent] = useState(false);

  const handleClick = useCallback(async () => {
    if (!parentEventId) return;
    setIsLoadingParent(true);
    try {
      const events = await nostr.query(
        [{ ids: [parentEventId], limit: 1 }],
        { signal: AbortSignal.timeout(5000) },
      );
      const parentPic = events.find((e) => e.kind === 20 && getImageUrl(e));
      if (parentPic) {
        onOpenParent(parentPic);
      }
    } catch {
      // Silently fail — couldn't fetch parent
    } finally {
      setIsLoadingParent(false);
    }
  }, [parentEventId, nostr, onOpenParent]);

  return (
    <div
      className={cn(
        'bg-polaroid-white p-3 shadow-[0_1px_6px_rgba(45,27,78,0.08)] transition-all',
        parentEventId && 'cursor-pointer hover:shadow-[0_3px_10px_rgba(45,27,78,0.14)] hover:-translate-y-px',
        isLoadingParent && 'opacity-70',
      )}
      style={{ transform: `rotate(${(seedFromHex(event.id, 0, 4) % 3) - 1}deg)` }}
      onClick={parentEventId ? handleClick : undefined}
    >
      {/* Author row */}
      <div className="flex items-center gap-2 mb-1.5">
        <Link
          to={`/${npub}`}
          className="flex items-center gap-2 group"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="w-6 h-6 rounded-full overflow-hidden bg-cream-dark border border-pol-pink/20 flex-shrink-0">
            {metadata?.picture ? (
              <img src={metadata.picture} alt={displayName} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-pol-pink/30 to-pol-lilac/30" />
            )}
          </div>
          <span className="font-hand text-sm text-pol-text-secondary group-hover:text-pol-text transition-colors">{displayName}</span>
        </Link>
        <span className="font-hand text-xs text-pol-text-caption ml-auto">{date}</span>
      </div>
      {/* Content */}
      <div className="text-sm text-pol-text whitespace-pre-wrap break-words line-clamp-3">
        <NoteContent event={event} className="text-sm" />
      </div>
      {/* "View photo" hint */}
      {parentEventId && (
        <p className="font-hand text-xs text-pol-pink mt-1.5 flex items-center gap-1">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <rect x="1" y="1" width="8" height="10" fill="none" stroke="currentColor" strokeWidth="1.2" />
            <rect x="2" y="2" width="6" height="5" fill="currentColor" opacity="0.2" />
          </svg>
          {isLoadingParent ? 'Loading...' : 'View photo'}
        </p>
      )}
    </div>
  );
}

/** Empty state with scrapbook vibe */
function EmptySearchState({ query, type }: { query: string; type: 'posts' | 'accounts' | 'prompt' }) {
  return (
    <div className="py-12 px-4 text-center">
      {/* Hand-drawn magnifying glass */}
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="mx-auto mb-3 opacity-30">
        <circle cx="20" cy="20" r="14" stroke="currentColor" strokeWidth="3" className="text-pol-text" />
        <path d="M30 31 L44 44" stroke="currentColor" strokeWidth="4" strokeLinecap="round" className="text-pol-text" />
      </svg>
      {type === 'prompt' ? (
        <p className="font-hand text-lg text-pol-text-secondary">
          Type to search for photos & people...
        </p>
      ) : type === 'posts' ? (
        <p className="font-hand text-lg text-pol-text-secondary">
          No photos or comments found for "{query}"
        </p>
      ) : (
        <p className="font-hand text-lg text-pol-text-secondary">
          No people found for "{query}"
        </p>
      )}
    </div>
  );
}

/** Skeleton for a polaroid post */
function PostSkeleton() {
  return (
    <div className="bg-polaroid-white p-3 shadow-[0_1px_6px_rgba(45,27,78,0.08)]">
      <Skeleton className="w-full aspect-[4/5] bg-cream-dark" />
      <div className="mt-2 space-y-1.5">
        <Skeleton className="h-4 w-3/4 bg-cream-dark" />
        <Skeleton className="h-3 w-1/2 bg-cream-dark" />
      </div>
    </div>
  );
}

/** Skeleton for an account */
function AccountSkeleton() {
  return (
    <div className="flex items-center gap-3 px-3 py-3 bg-polaroid-white shadow-[0_1px_6px_rgba(45,27,78,0.08)]">
      <Skeleton className="w-11 h-11 rounded-full bg-cream-dark" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-4 w-28 bg-cream-dark" />
        <Skeleton className="h-3 w-36 bg-cream-dark" />
      </div>
    </div>
  );
}
